import { Activity, CheckCircle2, Database, Monitor } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { TradeIdeasHealth, TradeIdeasStatus } from "@/lib/tradeideas/types";

export function StatusSummary({
  status,
  health,
}: {
  status: TradeIdeasStatus;
  health: TradeIdeasHealth | undefined;
}) {
  return (
    <div className="grid gap-3 lg:grid-cols-4">
      <SummaryCard
        icon={Activity}
        label="Runtime"
        value={status.mode}
        detail={status.polling_enabled ? "polling enabled" : "polling paused"}
        tone={status.mode === "active" && status.polling_enabled ? "ok" : "warn"}
      />
      <SummaryCard
        icon={CheckCircle2}
        label="Health"
        value={status.status}
        detail={health?.ready ? "ready" : "not ready"}
        tone={status.status === "ok" ? "ok" : status.status === "critical" ? "error" : "warn"}
      />
      <SummaryCard
        icon={Database}
        label="Alerts"
        value={status.stats.alerts_total.toLocaleString()}
        detail={`${status.outbox.pending + status.outbox.retry} queued`}
        tone={status.outbox.dead > 0 ? "error" : status.outbox.pending + status.outbox.retry > 0 ? "warn" : "ok"}
      />
      <SummaryCard
        icon={Monitor}
        label="Host"
        value={status.host.alias || status.host.hostname || "unknown"}
        detail={status.host.os ? `${status.host.os} ${status.host.arch ?? ""}` : "machine"}
        tone="neutral"
      />
    </div>
  );
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  detail,
  tone,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  detail: string;
  tone: "ok" | "warn" | "error" | "neutral";
}) {
  return (
    <Card className="rounded-md">
      <CardHeader className="pb-2">
        <CardDescription className="flex items-center gap-2">
          <Icon className={iconClass(tone)} />
          {label}
        </CardDescription>
        <CardTitle className="truncate text-2xl tabular-nums">{value}</CardTitle>
      </CardHeader>
      <CardContent>
        <Badge variant={badgeVariant(tone)}>{detail}</Badge>
      </CardContent>
    </Card>
  );
}

function iconClass(tone: "ok" | "warn" | "error" | "neutral"): string {
  const base = "size-4";
  if (tone === "ok") return `${base} text-emerald-300`;
  if (tone === "warn") return `${base} text-amber-300`;
  if (tone === "error") return `${base} text-red-300`;
  return `${base} text-muted-fg`;
}

function badgeVariant(tone: "ok" | "warn" | "error" | "neutral") {
  if (tone === "ok") return "success";
  if (tone === "warn") return "warn";
  if (tone === "error") return "danger";
  return "outline";
}
